/**
 * Types shared between the ingest server and the web client.
 * Everything here must stay JSON-serialisable: it travels over the API and
 * through the SSE stream.
 */
export * from './enums.js';
/** Node numbers appear on the wire and in MQTT topics as `!deadbeef`. */
export function nodeNumToHex(nodeNum) {
    return `!${(nodeNum >>> 0).toString(16).padStart(8, '0')}`;
}
export function hexToNodeNum(hex) {
    if (!hex)
        return null;
    const clean = hex.startsWith('!') ? hex.slice(1) : hex;
    if (!/^[0-9a-fA-F]{1,8}$/.test(clean))
        return null;
    return Number.parseInt(clean, 16) >>> 0;
}
/**
 * Rasterweite der Hex-Ebenen.
 *
 * Beide Kachelraster -- die Tiles und H3 -- halbieren ihre Kantenlaenge alle
 * zwei Zoomstufen. Damit bleibt eine Zelle beim Zoomen ungefaehr gleich gross auf
 * dem Bildschirm, wenn die Aufloesung denselben Schritt macht. Zu weit draussen
 * waeren Res-8-Zellen kleiner als ein Pixel, zu weit drinnen waeren Res-6-Zellen
 * ganze Bildschirmviertel.
 */
export const HEX_RESOLUTION_MIN = 6;
export const HEX_RESOLUTION_MAX = 10;
/** Loest eine Zoomstufe in die H3-Aufloesung der Hex-Ebenen auf. */
export function hexResolutionForZoom(zoom) {
    if (!Number.isFinite(zoom))
        return 7;
    return clampHexResolution(7 + Math.floor((zoom - 8) / 2));
}
/** Haelt eine angefragte Aufloesung im Bereich, den Client und Server teilen. */
export function clampHexResolution(value, fallback = 7) {
    if (!Number.isFinite(value))
        return fallback;
    const rounded = Math.round(value);
    return Math.min(HEX_RESOLUTION_MAX, Math.max(HEX_RESOLUTION_MIN, rounded));
}
/** Ungefaehre Zellflaeche einer H3-Zelle in km^2. */
export function hexCellAreaKm2(resolution) {
    // Die mittlere Zellflaeche schrumpft pro Aufloesungsstufe um rund den Faktor
    // 5.9; Res 7 misst etwa 8 km^2.
    return 8.05 * 5.9 ** (7 - clampHexResolution(resolution));
}
/** Ungefaehre Kantenlaenge einer H3-Zelle in km, nur fuer die Anzeige. */
export function hexCellKm(resolution) {
    // Aus der Flaeche folgt die Laenge ueber Ecken.
    return 1.24 * Math.sqrt(hexCellAreaKm2(resolution));
}
/**
 * Radius der Dichte-Blase in Metern.
 *
 * Die Dichte wird als Blase gezeichnet, nicht als Hexagon: der Server
 * aggregiert weiter in H3-Zellen, nur der Client rundet sie ab. Der Radius folgt
 * der Zellflaeche, damit die Blase denselben Platz auf der Karte einnimmt wie das
 * Hexagon, das sie ersetzt -- die Dichte steckt damit allein in der Fuellung.
 */
export function densityBubbleRadiusM(resolution) {
    return 1000 * Math.sqrt(hexCellAreaKm2(resolution) / Math.PI);
}
export const SIGNAL_BANDS = [
    { band: 'excellent', min: -80, color: '#22c55e', label: 'Excellent' },
    { band: 'good', min: -100, color: '#84cc16', label: 'Good' },
    { band: 'ok', min: -115, color: '#eab308', label: 'OK' },
    { band: 'weak', min: -125, color: '#f97316', label: 'Weak' },
    { band: 'marginal', min: -999, color: '#3b82f6', label: 'Marginal' },
];
export function signalBand(rssi) {
    if (rssi === null || rssi === undefined)
        return null;
    for (const b of SIGNAL_BANDS)
        if (rssi >= b.min)
            return b.band;
    return 'marginal';
}
export function signalColor(rssi) {
    const band = signalBand(rssi);
    if (!band)
        return '#64748b';
    return SIGNAL_BANDS.find((b) => b.band === band).color;
}
export function signalLabel(rssi) {
    const band = signalBand(rssi);
    if (!band)
        return 'unknown';
    return SIGNAL_BANDS.find((b) => b.band === band).label;
}
/**
 * Coverage colouring is SNR-based, not RSSI-based.
 *
 * Meshtastic does not populate MeshPacket.rx_rssi on the receive path (the
 * firmware only reads it for debug output), so a Bluetooth mapper can never
 * see an RSSI. SNR is what it does get, and it is what actually decides
 * whether a packet decodes at all.
 */
export const COVERAGE_BANDS = [
    { min: 8, color: '#22c55e', label: 'Sehr gut' },
    { min: 4, color: '#84cc16', label: 'Gut' },
    { min: 0, color: '#eab308', label: 'Brauchbar' },
    { min: -6, color: '#f97316', label: 'Schwach' },
    { min: -99, color: '#ef4444', label: 'Kaum Empfang' },
];
export function coverageBand(snr) {
    if (snr === null || snr === undefined)
        return null;
    for (const b of COVERAGE_BANDS)
        if (snr >= b.min)
            return b;
    return COVERAGE_BANDS[COVERAGE_BANDS.length - 1];
}
export function coverageColor(snr) {
    return coverageBand(snr)?.color ?? '#64748b';
}
/** A node counts as online if we heard something from it this recently. */
export const ONLINE_WINDOW_MS = 15 * 60 * 1000;
export function isOnline(lastHeard, now = Date.now()) {
    return now - lastHeard <= ONLINE_WINDOW_MS;
}
export function formatAge(seconds) {
    if (seconds < 0)
        return 'gerade eben';
    if (seconds < 60)
        return `vor ${Math.floor(seconds)} s`;
    if (seconds < 3600)
        return `vor ${Math.floor(seconds / 60)} min`;
    if (seconds < 86400)
        return `vor ${Math.floor(seconds / 3600)} h`;
    if (seconds < 31536000)
        return `vor ${Math.floor(seconds / 86400)} d`;
    return `vor ${Math.floor(seconds / 31536000)} J`;
}
/**
 * Position precision bits -> ground radius in metres.
 *
 * Meshtastic stores latitude/longitude as decimal degrees scaled by 1e7 in an
 * int32. Truncating to `bits` significant bits leaves a step of
 * 2^(32 - bits) in those units; the worst-case error is half a step.
 *
 * Verified against the table in the Meshtastic docs
 * (docs/configuration/radio/channels -> Position Precision):
 * 10 -> 23.3 km, 13 -> 2.9 km, 15 -> 729 m, 19 -> 45 m.
 *
 * The public MQTT broker filters out precise positions, so nodes seen here
 * usually sit in the 10-15 bit range and cover kilometres.
 */
export function precisionRadiusM(bits) {
    if (!bits || bits <= 0 || bits > 32)
        return null;
    const stepDeg = 2 ** (32 - bits) / 1e7;
    return Math.round((stepDeg * 111320) / 2);
}
export function batteryColor(level) {
    if (level === null || level === undefined)
        return '#64748b';
    if (level > 70)
        return '#22c55e';
    if (level > 40)
        return '#eab308';
    if (level > 15)
        return '#f97316';
    return '#ef4444';
}
/** Default map view: mainland Europe, where most EU868 nodes live. */
export const DEFAULT_VIEW = {
    center: [50.5, 9.0],
    zoom: 5,
    bounds: {
        south: 44.0,
        west: -1.0,
        north: 55.5,
        east: 16.0,
    },
};
//# sourceMappingURL=index.js.map